"use client";
import {
  occupationalDetailsSchema,
  occupationalDetailsType,
  otherDetailsSchema,
  otherDetailsType,
  recentIllnessDetailsSchema,
  recentIllnessDetailsType,
} from "@/src/mocks";
import { reduceTypeToObj } from "@/src/utils/generic-utils";
import { FormProps } from "./form-one";
import FormContainer, { FormSubSectionTitle } from "./form-container";
import BaseButton from "@/src/components/landing-page/button/base-button";
import { Controller } from "react-hook-form";
import {
  BaseDropdown,
  CheckRadioButtonGroup,
  CreatableDropdown,
  FloatingLabelInput,
  RadioButtonGroup,
} from "@/src/components";
import { handleParentFormChange } from "@/src/utils/storage";
import { useQuery } from "@tanstack/react-query";
import { getBanks } from "@/src/services";

const occupationalDetailsLabel = [
  "Occupation",
  "Gross annual Income",
  "Input your source of funds",
  "Frequency of payment",
  "Employment status",
  "Business/Occupation",
  "Business employers name",
  "Business employers address",
  "Bank name",
  "Account number",
  "Account Type",
];

const otherDetailsLabel = [
  "Have any applications for Life, Accident, or Health Insurance ever been declined, postponed, or modified?",
  "What life insurance do you have?",
  "Year issued?",
  "Name of company",
  "Enter amount",
  "Any deformity?",
  "Height in CM?",
  "Weight in KG?",
];
const recentIllnessDetailsLabel = [
  "Been ill?",
  "Consulted a physician",
  "Been under consultation for any medical condition",
];

const occupationalDetailsLabelMap = reduceTypeToObj<occupationalDetailsType>(
  occupationalDetailsLabel,
  occupationalDetailsSchema.describe().fields
);
const otherDetailsLabelMap = reduceTypeToObj<otherDetailsType>(
  otherDetailsLabel,
  otherDetailsSchema.describe().fields
);

const recentIllnessDetailsLabelMap = reduceTypeToObj<recentIllnessDetailsType>(
  recentIllnessDetailsLabel,
  recentIllnessDetailsSchema.describe().fields
);

const occupationOptions = [
  { label: "Private sector service", value: "private_sector_service" },
  { label: "Public sector", value: "public_sector" },
  { label: "Government services", value: "government_services" },
  { label: "Business", value: "business" },
  { label: "Professional", value: "professional" },
  { label: "Agriculturist", value: "agriculturist" },
  { label: "Retired", value: "retired" },
  { label: "Housewife", value: "housewife" },
  { label: "Student", value: "student" },
  { label: "Forex dealer", value: "forex_dealer" },
  { label: "Others", value: "others" }, // additional textbox for custom input can be added when 'Others' is selected
];

const grossAnnualIncomeOptions = [
  { label: "Below ₦1 Million", value: "below_1_million" },
  { label: "₦2-5 Million", value: "2_5_million" },
  { label: "₦6-10 Million", value: "6_10_million" },
  { label: "₦11-20 Million", value: "11_20_million" },
  { label: "₦20 Million - above", value: "20_million_above" },
];

const frequencyOfPaymentOptions = [
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Half Yearly", value: "half_yearly" },
  { label: "Yearly", value: "yearly" },
];

const employmentStatusOptions = [
  { label: "Paid - Employed", value: "paid_employed" },
  { label: "Self - Employed", value: "self_employed" },
  { label: "Others", value: "others" }, // additional textbox for custom input can be added when 'Others' is selected
];

const accountTypeOptions = [
  { label: "Savings account", value: "savings_account" },
  { label: "Current account", value: "current_account" },
];

function FormTwo({
  nextStep,
  trigger,
  getValues,
  control,
  register,
  errors,
  setValue,
  currentEmployment,
  currentPayment,
  currentIncome,
  currentOccupation,
  currentAccount,
  currentBank,
  setCurrentOccupation,
  setCurrentPayment,
  setCurrentEmployment,
  setCurrentIncome,
  setCurrentAccount,
  setCurrentBank,
}: FormProps) {
  const dotNotLabel = [
    ...Object.keys(occupationalDetailsSchema.describe().fields).map((el) => el),
    ...Object.keys(otherDetailsSchema.describe().fields).map((el) => el),
    ...Object.keys(recentIllnessDetailsSchema.describe().fields).map(
      (el) => el
    ),
  ];

  const { data }: any = useQuery({
    queryKey: ["all-banks"],
    queryFn: getBanks,
  });

  const banksData = data?.data?.data?.map((el: { name: any; code: any }) => {
    return { label: el.name, value: el.code };
  });

  async function onValidate(): Promise<void> {
    const values = getValues();

    try {
      await occupationalDetailsSchema.validate(values, { abortEarly: false });
      await otherDetailsSchema.validate(values, { abortEarly: false });
      await recentIllnessDetailsSchema.validate(values, { abortEarly: false });

      nextStep();
    } catch (error: any) {
      // @ts-ignore
      trigger(dotNotLabel);
      console.error(error.inner);
    }
  }

  return (
    <section>
      <FormContainer
        title="Tell us more about employment"
        subTitle="Please let's have a glimpse of what you do for work"
      >
        <form>
          <div className="py-2">
            <FormSubSectionTitle title="Occupational details" />
            <div className="my-6">
              <div className="mb-5">
                <Controller
                  control={control}
                  name={"occupation"}
                  render={() => (
                    <CreatableDropdown
                      w="100%"
                      placeholder={occupationalDetailsLabelMap.occupation}
                      onChange={handleParentFormChange(
                        "occupation",
                        getValues,
                        setValue,
                        trigger,
                        setCurrentOccupation
                      )}
                      defaultValue={occupationOptions.find(
                        (opt) => opt.value === currentOccupation?.value
                      )}
                      error={errors?.occupation?.message}
                      options={occupationOptions}
                    />
                  )}
                />
              </div>
              <div className="mb-5">
                <Controller
                  control={control}
                  name={"grossAnnualIncome"}
                  render={() => (
                    <BaseDropdown
                      w="100%"
                      placeholder={
                        occupationalDetailsLabelMap.grossAnnualIncome
                      }
                      onChange={handleParentFormChange(
                        "grossAnnualIncome",
                        getValues,
                        setValue,
                        trigger,
                        setCurrentIncome
                      )}
                      defaultValue={grossAnnualIncomeOptions.find(
                        (opt) => opt.value === currentIncome?.value
                      )}
                      error={errors.grossAnnualIncome?.message}
                      options={grossAnnualIncomeOptions}
                    />
                  )}
                />
              </div>
              <div className="mb-5">
                <FloatingLabelInput
                  label={occupationalDetailsLabelMap.sourceOfFunds}
                  id="occupationalDetails.sourceOfFunds"
                  placeholder={occupationalDetailsLabelMap.sourceOfFunds}
                  {...register("sourceOfFunds")}
                  error={errors?.sourceOfFunds?.message}
                />
              </div>
              <div className="mb-5">
                <Controller
                  control={control}
                  name={"frequencyOfPayment"}
                  render={() => (
                    <BaseDropdown
                      w="100%"
                      placeholder={
                        occupationalDetailsLabelMap.frequencyOfPayment
                      }
                      onChange={handleParentFormChange(
                        "frequencyOfPayment",
                        getValues,
                        setValue,
                        trigger,
                        setCurrentPayment
                      )}
                      defaultValue={frequencyOfPaymentOptions.find(
                        (opt) => opt.value === currentPayment?.value
                      )}
                      error={errors?.frequencyOfPayment?.message}
                      options={frequencyOfPaymentOptions}
                    />
                  )}
                />
              </div>
              <div className="mb-5">
                <Controller
                  control={control}
                  name={"employmentStatus"}
                  render={() => (
                    <CreatableDropdown
                      w="100%"
                      placeholder={occupationalDetailsLabelMap.employmentStatus}
                      onChange={handleParentFormChange(
                        "employmentStatus",
                        getValues,
                        setValue,
                        trigger,
                        setCurrentEmployment
                      )}
                      defaultValue={employmentStatusOptions.find(
                        (opt) => opt.value === currentEmployment?.value
                      )}
                      error={errors?.employmentStatus?.message}
                      options={employmentStatusOptions}
                    />
                  )}
                />
              </div>

              <div className="mb-5">
                <FloatingLabelInput
                  label={occupationalDetailsLabelMap.businessName}
                  id="occupationalDetails.businessOccupation"
                  placeholder={occupationalDetailsLabelMap.businessName}
                  {...register("businessName")}
                  error={errors?.businessName?.message}
                />
              </div>
              <div className="mb-5">
                <FloatingLabelInput
                  label={occupationalDetailsLabelMap.businessEmployersName}
                  id="occupationalDetails.businessEmployersName"
                  placeholder={
                    occupationalDetailsLabelMap.businessEmployersName
                  }
                  {...register("businessEmployersName")}
                  error={errors?.businessEmployersName?.message}
                />
              </div>

              <div className="mb-5">
                <FloatingLabelInput
                  label={occupationalDetailsLabelMap.businessAddress}
                  id="occupationalDetails.businessAddress"
                  placeholder={occupationalDetailsLabelMap.businessAddress}
                  {...register("businessAddress")}
                  error={errors.businessAddress?.message}
                />
              </div>
              <div className="mb-5">
                <Controller
                  control={control}
                  name={"bankName"}
                  render={() => (
                    <BaseDropdown
                      w="100%"
                      placeholder={occupationalDetailsLabelMap.bankName}
                      onChange={handleParentFormChange(
                        "bankName",
                        getValues,
                        setValue,
                        trigger,
                        setCurrentBank
                      )}
                      defaultValue={banksData?.find(
                        (opt: any) => opt.value === currentBank?.value
                      )}
                      error={errors?.bankName?.message}
                      options={banksData}
                    />
                  )}
                />
              </div>
              <div className="mb-5">
                <FloatingLabelInput
                  label={occupationalDetailsLabelMap.bankAccountNumber}
                  id="occupationalDetails.bankAccountNumber"
                  type="number"
                  placeholder={occupationalDetailsLabelMap.bankAccountNumber}
                  {...register("bankAccountNumber")}
                  error={errors?.bankAccountNumber?.message}
                />
              </div>
              <div className="mb-5">
                <Controller
                  control={control}
                  name={"accountType"}
                  render={() => (
                    <BaseDropdown
                      w="100%"
                      placeholder={occupationalDetailsLabelMap.accountType}
                      onChange={handleParentFormChange(
                        "accountType",
                        getValues,
                        setValue,
                        trigger,
                        setCurrentAccount
                      )}
                      defaultValue={accountTypeOptions.find(
                        (opt: any) => opt.value === currentAccount?.value
                      )}
                      error={errors?.accountType?.message}
                      options={accountTypeOptions}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div className="py-2">
            <FormSubSectionTitle title="Other details details" />
            <div className="my-6 ">
              <div className="mb-5">
                <CheckRadioButtonGroup
                  name={"insuranceApplicationPostponedDeclined"}
                  id={"otherDetails.insuranceApplicationPostponedDeclined"}
                  control={control}
                  label={
                    otherDetailsLabelMap.insuranceApplicationPostponedDeclined
                  }
                  options={[
                    { label: "Yes", value: true },
                    { label: "No", value: false },
                  ]}
                />
              </div>
              <div className="mb-5">
                <FloatingLabelInput
                  label={otherDetailsLabelMap.currentLifeInsurance}
                  id="otherDetails.lifeInsuranceType"
                  {...register("currentLifeInsurance")}
                  error={errors?.currentLifeInsurance?.message}
                />
              </div>
              <div className="mb-5">
                <FloatingLabelInput
                  label={otherDetailsLabelMap.currentLifeInsuranceIssueYear}
                  id="otherDetails.currentLifeInsuranceIssueYear"
                  type="number"
                  {...register("currentLifeInsuranceIssueYear")}
                  error={errors.currentLifeInsuranceIssueYear?.message}
                />
              </div>
              <div className="mb-5">
                <FloatingLabelInput
                  label={otherDetailsLabelMap.insuranceProvider}
                  id="otherDetails.insuranceProvider"
                  {...register("insuranceProvider")}
                  error={errors.insuranceProvider?.message}
                />
              </div>
              <div className="mb-5">
                <FloatingLabelInput
                  label={otherDetailsLabelMap.insuranceAmount}
                  id="otherDetails.insuranceAmount"
                  type="number"
                  {...register("insuranceAmount")}
                  error={errors.insuranceAmount?.message}
                />
              </div>
              <div className="mb-5">
                <CheckRadioButtonGroup
                  name={"hasDeformity"}
                  id={"otherDetails.hasDeformity"}
                  control={control}
                  label={otherDetailsLabelMap.hasDeformity}
                  options={[
                    { label: "Yes", value: true },
                    { label: "No", value: false },
                  ]}
                />
              </div>
              <div className="mb-5 flex justify-between gap-3">
                <FloatingLabelInput
                  label={otherDetailsLabelMap.height}
                  id="otherDetails.height"
                  type="number"
                  step="0.01"
                  placeholder={otherDetailsLabelMap.height}
                  {...register("height")}
                  error={errors.height?.message}
                />
                <FloatingLabelInput
                  label={otherDetailsLabelMap.weight}
                  id="otherDetails.weight"
                  type="number"
                  step="0.01"
                  placeholder={otherDetailsLabelMap.weight}
                  {...register("weight")}
                  error={errors.weight?.message}
                />
              </div>
            </div>
          </div>

          <div className="py-2">
            <FormSubSectionTitle title="Have you been ill the last five (5) years?" />
            <div className="my-6">
              <div className="mb-5">
                <CheckRadioButtonGroup
                  name={"illInLastFiveYears"}
                  id={"recentIllnessDetails.illInLastFiveYears"}
                  control={control}
                  label={recentIllnessDetailsLabelMap.illInLastFiveYears}
                  options={[
                    { label: "Yes", value: true },
                    { label: "No", value: false },
                  ]}
                />
              </div>
              <div className="mb-5">
                <CheckRadioButtonGroup
                  name={"consultedPhysicianInLastFiveYears"}
                  id={"recentIllnessDetails.consultedPhysicianInLastFiveYears"}
                  control={control}
                  label={
                    recentIllnessDetailsLabelMap.consultedPhysicianInLastFiveYears
                  }
                  options={[
                    { label: "Yes", value: true },
                    { label: "No", value: false },
                  ]}
                />
              </div>
              <div className="mb-5">
                <CheckRadioButtonGroup
                  name={"underMedicalConsultationInLastFiveYears"}
                  id={
                    "recentIllnessDetails.underMedicalConsultationInLastFiveYears"
                  }
                  control={control}
                  label={
                    recentIllnessDetailsLabelMap.underMedicalConsultationInLastFiveYears
                  }
                  options={[
                    { label: "Yes", value: true },
                    { label: "No", value: false },
                  ]}
                />
              </div>
            </div>
          </div>
          <div className="my-6">
            <BaseButton onClick={onValidate} fullWidth>
              Proceed
            </BaseButton>
          </div>
        </form>
      </FormContainer>
    </section>
  );
}

export default FormTwo;
